import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from '../utils/Seo';
import { useData } from '../contexts/DataContext';
import FloatingActions from './FloatingActions';
import { LandingNav, LandingFooter, MobileDock, DiamondCursor } from './landing/LandingChrome';
import {
  LandingHero, Marquee, AboutBlock, ServicesBlock, WorksBlock, GalleryBlock, FilmsBlock, MottoBand,
  ProcessBlock, PackagesBlock, GuestsBlock, SystemsBlock, ReviewsBlock, FaqBlock, FinalCta, ContactBlock,
} from './landing/LandingSections';
import { useFeaturedProjects, useLatestFilms, usePublishedReviews } from './landing/useLandingCollections';
import useLandingMotion from './landing/useLandingMotion';
import { scrollToId, useLang } from './landing/landingUtils';
import './landing/landing.css';

function LandingPage() {
  const { settings, categories, featuredPackages, isInitialLoading } = useData();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const lang = useLang();
  const rootRef = useRef(null);
  const projects = useFeaturedProjects();
  const films = useLatestFilms();
  const reviews = usePublishedReviews();
  // The hero intro waits for the opening curtain only on the visit that showed it.
  const [introDelay] = useState(() => {
    const shown = typeof window !== 'undefined' && window.__icaSplashShown;
    if (shown) window.__icaSplashShown = false;
    return shown ? '2.9s' : '.15s';
  });

  useLandingMotion(rootRef, [
    isInitialLoading, lang, projects.length, films.length, reviews.length,
    featuredPackages?.length, settings?.galleryImages?.length, settings?.services?.length,
  ].join('|'));

  // Lock page scroll behind the full-screen mobile menu.
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  // Section links from other pages navigate here with { state: { scrollTo } }.
  useEffect(() => {
    const id = location.state?.scrollTo;
    if (!id) return;
    const t = setTimeout(() => scrollToId(id), 400);
    return () => clearTimeout(t);
  }, [location.state]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  // Pre-select a package and scroll to contact when arriving from /paketi/:id
  // (user clicks "Zatraži ponudu" → navigate('/?package=<id>', { state: { selectedPackage } }))
  useEffect(() => {
    if (isInitialLoading) return;

    const params = new URLSearchParams(location.search);
    const pkgId = params.get('package');
    const stateSelected = location.state?.selectedPackage || null;

    if (!pkgId && !stateSelected) return;

    if (stateSelected) {
      setSelectedPackage(stateSelected);
    } else if (pkgId) {
      const pkg = (featuredPackages || []).find(p => p.firestoreId === pkgId);
      if (pkg) setSelectedPackage(pkg);
    }

    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);

    navigate('/', { replace: true });
  }, [isInitialLoading, featuredPackages, location.search, location.state, navigate]);

  useEffect(() => {
    // Hash scroll handling for in-page navigation from other routes.
    const checkHash = () => {
      const hash = window.location.hash;
      if (hash) {
        setTimeout(() => {
          const id = hash.replace('#', '');
          const element = document.getElementById(id);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 500);
      }
    };

    checkHash();
    window.addEventListener('hashchange', checkHash);
    return () => window.removeEventListener('hashchange', checkHash);
  }, []);

  // Note: LandingPage intentionally renders immediately (no loader gate) so the
  // hero paints as fast as possible for LCP. Sections fall back to sensible
  // defaults while settings stream in from Firestore via DataContext.

  // SEO + sharing meta — built dynamically from settings so any change in admin
  // (site name, hero title/subtitle, hero images, contact info) is reflected
  // in OG tags and JSON-LD structured data without code changes.
  const siteUrl = (typeof window !== 'undefined' && window.location?.origin) || 'https://infinitycreativeagencybih.web.app';
  const siteName = settings?.siteName || 'Infinity Creative Agency';
  const heroTitleBs = settings?.heroTitleBs || 'Infinity Creative Agency';
  const heroSubtitleBs = settings?.heroSubtitleBs || 'Vaša priča, naš objektiv';
  const seoTitle = `${siteName} — ${heroSubtitleBs}`;
  const seoDescription =
    settings?.heroMottoBs ||
    settings?.aboutDescriptionBs ||
    'Profesionalna fotografija, video produkcija i digitalna rješenja. Godinama gradimo kompletne sisteme za kreativce, vjenčanja i biznise u BiH.';

  // Shared links show the brand card (mark + wordmark), not a hero photo.
  const heroImage = '/brand/infinity-og.png';
  const ogImage = heroImage.startsWith('http')
    ? encodeURI(heroImage)
    : encodeURI(`${siteUrl}${heroImage.startsWith('/') ? '' : '/'}${heroImage}`);

  const phoneRaw = settings?.contactPhone || settings?.phone || '+387 61 680 413';
  const emailRaw = settings?.contactEmail || settings?.email || 'selimovicerminphoto@gmail.com';
  const instagramUrl = settings?.instagramUrl || settings?.instagram || '';
  const facebookUrl = settings?.facebook || '';

  // LocalBusiness schema — gives Google rich result with rating, address, hours.
  const localBusinessJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${siteUrl}/#business`,
    name: siteName,
    description: seoDescription,
    url: siteUrl,
    image: ogImage,
    telephone: phoneRaw,
    email: emailRaw,
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'BA',
      addressRegion: 'Tuzlanski Kanton',
      addressLocality: 'Tuzla',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 44.5382,
      longitude: 18.6734,
    },
    areaServed: { '@type': 'Country', name: 'Bosnia and Herzegovina' },
    priceRange: '$$',
    sameAs: [instagramUrl, facebookUrl].filter(Boolean),
  };

  // WebSite schema — enables sitelinks search box in Google.
  const websiteJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    url: siteUrl,
    inLanguage: ['bs-BA', 'en-US'],
    publisher: { '@id': `${siteUrl}/#business` },
  };

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="theme-color" content="#0A0A0A" />
        <link rel="canonical" href={siteUrl + '/'} />

        {/* Open Graph (Facebook, WhatsApp, LinkedIn previews) */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={siteName} />
        <meta property="og:url" content={siteUrl + '/'} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:alt" content={`${siteName} — ${heroTitleBs}`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="bs_BA" />
        <meta property="og:locale:alternate" content="en_US" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={ogImage} />

        {/* JSON-LD: LocalBusiness + WebSite */}
        <script type="application/ld+json" data-helmet="local-business">
          {JSON.stringify(localBusinessJsonLd)}
        </script>
        <script type="application/ld+json" data-helmet="website">
          {JSON.stringify(websiteJsonLd)}
        </script>
      </Helmet>

      <div ref={rootRef} className={`ica${isMenuOpen ? ' menu-open' : ''}`} style={{ '--intro': introDelay }}>
        <LandingNav settings={settings} lang={lang} menuOpen={isMenuOpen} setMenuOpen={setIsMenuOpen} />
        <main>
          <LandingHero settings={settings} lang={lang} />
          <Marquee settings={settings} categories={categories} lang={lang} />
          {settings?.showAbout !== false && <AboutBlock settings={settings} lang={lang} />}
          {settings?.showServices !== false && <ServicesBlock settings={settings} lang={lang} />}
          <WorksBlock settings={settings} lang={lang} projects={projects} />
          {settings?.showGallery !== false && <GalleryBlock settings={settings} lang={lang} />}
          {settings?.showFilms !== false && <FilmsBlock settings={settings} lang={lang} films={films} />}
          <MottoBand lang={lang} />
          <ProcessBlock lang={lang} />
          {settings?.showPackages !== false && (
            <PackagesBlock
              settings={settings}
              lang={lang}
              packages={featuredPackages}
              categories={categories}
              onSelect={(pkg) => { setSelectedPackage(pkg); scrollToId('contact'); }}
            />
          )}
          {settings?.showGuestGallery !== false && <GuestsBlock lang={lang} />}
          <SystemsBlock lang={lang} />
          {settings?.showReviews !== false && <ReviewsBlock settings={settings} lang={lang} reviews={reviews} />}
          {settings?.showFAQ !== false && <FaqBlock settings={settings} lang={lang} />}
          {settings?.showCTA !== false && <FinalCta settings={settings} lang={lang} />}
          <ContactBlock settings={settings} lang={lang} selectedPackage={selectedPackage} />
        </main>
        <LandingFooter settings={settings} lang={lang} />
        <MobileDock settings={settings} lang={lang} />
        <div className="ica-fab"><FloatingActions settings={settings} hidden={isMenuOpen} /></div>
        <DiamondCursor />
      </div>
    </>
  );
}

export default LandingPage;
